import { elementAttributes } from "../html-attributes";
import {
  isBlankColor,
  parseButtonBackground,
  parseButtonProperty,
  parseButtonStyle,
  parseTextAlign,
} from "../html-parse";
import { EMPTY_STYLE_MAP, type StyleMap } from "../shared-block-styles";
import { EmailBlockDefinition } from "../types";

import { LuMousePointerClick } from "react-icons/lu";

import { buttonEmailRenderer } from "./email-renderer";
import { ButtonTiptapView } from "./tiptap-view";

export interface ButtonAttrs extends Record<string, unknown> {
  text: string;
  href: string;
  backgroundColor: string;
  textColor: string;
  alignment: "left" | "center" | "right";
  borderRadius: string;
  borderWidth: string;
  borderStyle: string;
  borderColor: string;
  paddingHorizontal: string;
  paddingVertical: string;
  /** Inline styles as the markup wrote them — the block's style store. */
  style: StyleMap;
  /** Attributes the block does not otherwise model, kept rather than dropped. */
  attributes: Record<string, string>;
}

export const buttonBlock: EmailBlockDefinition<ButtonAttrs> = {
  name: "emailButton",
  label: "Button",
  icon: LuMousePointerClick,
  category: "interactive",

  schema: {
    attrs: {
      text: { default: "Click here" },
      href: { default: "" },
      backgroundColor: { default: "#006bb5" },
      textColor: { default: "#ffffff" },
      alignment: { default: "center" },
      borderRadius: { default: "12px" },
      borderWidth: { default: "0px" },
      borderStyle: { default: "solid" },
      borderColor: { default: "#000000" },
      paddingHorizontal: { default: "24px" },
      paddingVertical: { default: "8px" },
      style: { default: EMPTY_STYLE_MAP },
      attributes: { default: {} },
    },
    group: "emailBlock",
    draggable: true,
    isolating: true,
  },

  /*
    A call-to-action in email is an `<a>` painted to look like a button, so the
    only thing separating it from body copy is the background fill. Returning
    `false` for an unfilled link declines the rule, and the link falls through to
    the Link mark as ordinary inline text — which is what a plain link should be.

    The fill is often on the wrapping `<td>` rather than the link itself (Outlook
    won't paint a background on an `<a>`), so parseButtonBackground checks both;
    reading only the link's own style turned every bulletproof button in a real
    template into a paragraph.

    Priority beats the Link mark's default so a filled link becomes a real
    emailButton rather than a styled span of text.
  */
  parseHTML: () => [
    {
      tag: "a[href]",
      priority: 60,
      getAttrs: (node) => {
        const el = node as HTMLElement;
        const backgroundColor = parseButtonBackground(el);
        if (isBlankColor(backgroundColor)) return false;
        return {
          text: el.textContent?.trim() ?? "",
          href: el.getAttribute("href") ?? "",
          backgroundColor,
          textColor: el.style.color,
          alignment: parseTextAlign(el.parentElement ?? el),
          borderRadius: parseButtonProperty(el, "borderRadius"),
          borderWidth: parseButtonProperty(el, "borderWidth"),
          borderStyle: parseButtonProperty(el, "borderStyle"),
          borderColor: parseButtonProperty(el, "borderColor"),
          paddingHorizontal: el.style.paddingLeft,
          paddingVertical: el.style.paddingTop,
          style: parseButtonStyle(el),
          attributes: elementAttributes(el, ["href", "target", "rel"]),
        };
      },
    },
  ],

  tiptapComponent: ButtonTiptapView,
  emailRenderer: buttonEmailRenderer,
};
